import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectedDate, SelectedRange } from 'components/core/calendars/EventCalendar/types';
import dayjs from 'dayjs';
import { StoreNames } from 'store/types';

export type FullCalendar = {
  selectedDate: SelectedDate;
  selectedStartDate: undefined | Date;
  selectedEndDate: undefined | Date;
  selectedRange: SelectedRange;
};

export const initialFullCalendarState: FullCalendar = {
  selectedDate: {} as SelectedDate,
  selectedStartDate: undefined,
  selectedEndDate: undefined,
  selectedRange: {} as SelectedRange,
};

type SetSelectedDate = { selectedDate: SelectedDate };
type SetSelectedRange = { selectedRange: SelectedRange };
type SetDatesFromDate = { selectedDate: Date };
type SetDatesFromEvent = { startDate: Date; endDate: Date };
type SetDatesFromRange = { startDate: Date; endDate: Date };

type InitialFullCalendarSlice = typeof initialFullCalendarState;

export interface FullCalendarSlice extends InitialFullCalendarSlice {
  selectedStartDate: Date | undefined;
  selectedEndDate: Date | undefined;
}

const fullCalendarSlice = createSlice({
  name: StoreNames.fullCalendar,
  initialState: initialFullCalendarState,
  reducers: {
    setSelectedDate: (state, { payload: { selectedDate } }: PayloadAction<SetSelectedDate>) => {
      const startDay = dayjs(selectedDate.date);
      const endDay = startDay.add(1, 'day');

      state.selectedStartDate = startDay.toDate();
      state.selectedEndDate = endDay.toDate();
    },
    setStartDate: (state, { payload: { startDate } }: PayloadAction<{ startDate: Date }>) => {
      state.selectedStartDate = startDate;
    },
    setEndDate: (state, { payload: { date } }: PayloadAction<{ date: Date }>) => {
      state.selectedEndDate = date;
    },
    setDatesFromDate: (state, { payload: { selectedDate } }: PayloadAction<SetDatesFromDate>) => {
      const startDayJs = dayjs(selectedDate);
      const endDayJs = startDayJs.add(1, 'day');

      state.selectedStartDate = startDayJs.toDate();
      state.selectedEndDate = endDayJs.toDate();
    },
    setDatesFromEvent: (state, { payload: { startDate, endDate } }: PayloadAction<SetDatesFromEvent>) => {
      state.selectedStartDate = startDate;
      state.selectedEndDate = endDate;
    },
    setDatesFromRange: (state, { payload: { startDate, endDate } }: PayloadAction<SetDatesFromRange>) => {
      state.selectedStartDate = startDate;
      state.selectedEndDate = endDate;
    },

    setSelectedRange: (state, { payload: { selectedRange } }: PayloadAction<SetSelectedRange>) => {
      state.selectedRange = selectedRange;
    },
    resetDates: () => initialFullCalendarState,
  },
});

export const {
  reducer: fullCalendarReducer,
  actions,
  actions: {
    setSelectedDate,
    setSelectedRange,
    resetDates,
    setDatesFromDate,
    setDatesFromEvent,
    setDatesFromRange,
    setEndDate,
    setStartDate,
  },
} = fullCalendarSlice;
